import { useEffect, useState, useCallback } from "react";
import { getComments, addComment, updateComment, deleteComment } from "./CommentsService";
import { useToast } from "../ToastContext";
import "./CommentThread.css";

function fmtWhen(dt) {
  if (!dt) return "—";
  return new Date(dt).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" });
}

/**
 * Jira-style comment thread, reused across Task/Story/Epic detail views.
 * Newest comment always on top (backend returns createdAt desc; new posts are
 * unshifted client-side to match without a refetch).
 *
 * Props:
 *  - entityType: "TASK" | "STORY" | "EPIC"
 *  - entityId: string
 *  - compact: smaller composer/spacing for inline-expanded rows (default false)
 *  - onCountChange(count): optional — fired whenever the comment count changes,
 *    so a caller holding a list of rows (each with its own commentCount badge)
 *    can update that badge locally instead of refetching the whole list.
 */
export default function CommentThread({ entityType, entityId, compact = false, onCountChange }) {
  const toast = useToast();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    getComments(entityType, entityId)
      .then(data => setComments(data || []))
      .catch(() => toast("Failed to load comments", "error"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (onCountChange) onCountChange(comments.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comments.length]);

  const handlePost = async (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    setPosting(true);
    try {
      const created = await addComment(entityType, entityId, draft.trim());
      setComments(cs => [created, ...cs]);
      setDraft("");
    } catch {
      toast("Failed to post comment", "error");
    } finally {
      setPosting(false);
    }
  };

  const startEdit = (c) => { setEditingId(c.id); setEditDraft(c.content); };
  const cancelEdit = () => { setEditingId(null); setEditDraft(""); };

  const saveEdit = async (id) => {
    if (!editDraft.trim()) return;
    try {
      const updated = await updateComment(id, editDraft.trim());
      setComments(cs => cs.map(c => c.id === id ? updated : c));
      cancelEdit();
    } catch {
      toast("Failed to update comment", "error");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteComment(id);
      setComments(cs => cs.filter(c => c.id !== id));
      toast("Comment deleted", "success");
    } catch {
      toast("Failed to delete comment", "error");
    }
  };

  return (
    <div className={`cmt-thread ${compact ? "cmt-thread--compact" : ""}`}>
      <form className="cmt-composer" onSubmit={handlePost}>
        <textarea
          className="cmt-composer-input"
          rows={compact ? 2 : 3}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          placeholder="Add a comment…"
        />
        <button type="submit" className="cmt-composer-btn" disabled={posting || !draft.trim()}>
          {posting ? "Posting…" : "Comment"}
        </button>
      </form>

      {loading ? (
        <div className="cmt-empty">Loading comments…</div>
      ) : comments.length === 0 ? (
        <div className="cmt-empty">No comments yet — be the first to add one.</div>
      ) : (
        <div className="cmt-list">
          {comments.map(c => (
            <div key={c.id} className="cmt-item">
              {editingId === c.id ? (
                <div className="cmt-edit-form">
                  <textarea
                    className="cmt-composer-input"
                    rows={3}
                    value={editDraft}
                    onChange={e => setEditDraft(e.target.value)}
                    autoFocus
                  />
                  <div className="cmt-edit-actions">
                    <button type="button" className="cmt-btn-ghost" onClick={cancelEdit}>Cancel</button>
                    <button type="button" className="cmt-btn-primary" onClick={() => saveEdit(c.id)}>Save</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="cmt-item-header">
                    <span className="cmt-item-date">{fmtWhen(c.createdAt)}</span>
                    {c.updatedAt && c.updatedAt !== c.createdAt && (
                      <span className="cmt-item-edited">(edited {fmtWhen(c.updatedAt)})</span>
                    )}
                    <div className="cmt-item-actions">
                      <button className="cmt-item-action-btn" onClick={() => startEdit(c)} title="Edit comment">✏️</button>
                      <button className="cmt-item-action-btn cmt-item-del-btn" onClick={() => handleDelete(c.id)} title="Delete comment">🗑</button>
                    </div>
                  </div>
                  <div className="cmt-item-text">{c.content}</div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
